import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../utils/dateUtils';
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Users, Calendar } from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import FAQ from '../components/common/FAQ';
import { usePageMetadata } from '../hooks/usePageMetadata';

const PrivacyPolicy = () => {
    // Set page metadata
    usePageMetadata('privacy');

    const currentDate = formatDateUtil();

    return (
        <div className="min-h-screen bg-white">
            <Navbar />

            {/* Simple Header */}
            <div className="bg-white border-b border-gray-200 py-8 sm:py-12">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-4xl mx-auto">
                        <div className="flex items-center gap-2 sm:gap-4 mb-4 sm:mb-6">
                            <Link
                                to="/"
                                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200"
                            >
                                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                                <span className="text-xs sm:text-sm font-medium">Back to Home</span>
                            </Link>
                        </div>

                        <div>
                            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 text-gray-900">Privacy Policy</h1>
                            <p className="text-sm sm:text-base text-gray-600">Last updated: {currentDate}</p>
                        </div>

                        <p className="text-base sm:text-lg text-gray-700 leading-relaxed mt-4 sm:mt-6">
                            At Diasporawise, we are committed to protecting your privacy and ensuring the security of your personal information.
                            This Privacy Policy explains how we collect, use, and safeguard your data when you use our investment platform.
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="py-8 sm:py-12 md:py-16">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-4xl mx-auto">
                        <div className="prose prose-sm sm:prose-base md:prose-lg max-w-none">

                            {/* Information We Collect */}
                            <section className="mb-8 sm:mb-12">
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Information We Collect</h2>

                                <div className="space-y-6 text-gray-700 leading-relaxed">
                                    <p>
                                        We collect information you provide directly to us, such as when you create an account, complete your profile,
                                        make investments, or contact our support team. This may include your name, email address, phone number,
                                        date of birth, address, and financial information necessary for investment transactions.
                                    </p>

                                    <p>
                                        We also automatically collect certain information when you use our platform, including your IP address,
                                        browser type, device information, and usage patterns. This helps us improve our services and ensure
                                        platform security.
                                    </p>

                                    <p>
                                        Additionally, we may collect information from third-party sources, such as identity verification services
                                        and financial institutions, to comply with regulatory requirements and prevent fraud.
                                    </p>
                                </div>
                            </section>

                            {/* How We Use Your Information */}
                            <section className="mb-8 sm:mb-12">
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">How We Use Your Information</h2>

                                <div className="space-y-6 text-gray-700 leading-relaxed">
                                    <p>
                                        We use the information we collect to provide, maintain, and improve our investment platform services.
                                        This includes processing your investments, managing your account, and providing customer support.
                                    </p>

                                    <p>
                                        Your information helps us personalize your experience, send you relevant updates about your investments,
                                        and provide educational content about investment opportunities. We also use this data to ensure
                                        compliance with financial regulations and prevent fraudulent activities.
                                    </p>

                                    <p>
                                        We may use aggregated, anonymized data for research and analytics purposes to improve our platform
                                        and develop new features that benefit our community of investors.
                                    </p>
                                </div>
                            </section>

                            {/* Information Sharing */}
                            <section className="mb-8 sm:mb-12">
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Information Sharing and Disclosure</h2>

                                <div className="space-y-6 text-gray-700 leading-relaxed">
                                    <p>
                                        We do not sell, trade, or rent your personal information to third parties for marketing purposes.
                                        However, we may share your information in the following circumstances:
                                    </p>

                                    <p>
                                        <strong>Service Providers:</strong> We work with trusted third-party service providers who help us
                                        operate our platform, such as payment processors, identity verification services, and cloud hosting
                                        providers. These partners are contractually obligated to protect your information.
                                    </p>

                                    <p>
                                        <strong>Legal Requirements:</strong> We may disclose your information when required by law, such as
                                        in response to court orders, subpoenas, or other legal processes. We also share information with
                                        regulatory authorities as required by financial regulations.
                                    </p>

                                    <p>
                                        <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets,
                                        your information may be transferred as part of the business transaction, subject to the same
                                        privacy protections.
                                    </p>
                                </div>
                            </section>

                            {/* Data Security */}
                            <section className="mb-8 sm:mb-12">
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Data Security</h2>

                                <div className="space-y-6 text-gray-700 leading-relaxed">
                                    <p>
                                        We implement industry-standard security measures to protect your personal information from unauthorized
                                        access, alteration, disclosure, or destruction. This includes encryption of data in transit and at rest,
                                        regular security audits, and strict access controls.
                                    </p>

                                    <p>
                                        Our platform uses bank-level security protocols, including multi-factor authentication, secure
                                        socket layer (SSL) encryption, and regular penetration testing. We also maintain comprehensive
                                        backup systems to ensure data integrity and availability.
                                    </p>

                                    <p>
                                        While we strive to protect your information, no method of transmission over the internet or
                                        electronic storage is 100% secure. We encourage you to use strong passwords, enable two-factor
                                        authentication, and keep your account credentials confidential.
                                    </p>
                                </div>
                            </section>

                            {/* Your Rights */}
                            <section className="mb-8 sm:mb-12">
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Your Rights and Choices</h2>

                                <div className="space-y-6 text-gray-700 leading-relaxed">
                                    <p>
                                        You have the right to access, update, or delete your personal information at any time through
                                        your account settings. You can also request a copy of your data or ask us to restrict how we
                                        use your information.
                                    </p>

                                    <p>
                                        You can opt out of marketing communications by updating your preferences in your account settings
                                        or by clicking the unsubscribe link in our emails. However, we may still send you important
                                        service-related communications.
                                    </p>

                                    <p>
                                        If you have any questions about your privacy rights or would like to exercise them, please
                                        contact our privacy team at info@diasporawise.com. We will respond to your request within
                                        30 days.
                                    </p>
                                </div>
                            </section>

                            {/* Contact Information */}
                            <section className="mb-8 sm:mb-12">
                                <div className="bg-gray-50 rounded-lg p-4 sm:p-6 md:p-8 border border-gray-200">
                                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Contact Us</h3>
                                    <p className="text-gray-700 mb-4">
                                        If you have any questions about this Privacy Policy or our privacy practices, please contact us:
                                    </p>
                                    <div className="space-y-2 text-gray-700">
                                        <p><strong>Email:</strong> info@diasporawise.com</p>
                                        <p><strong>Address:</strong> 123 Investment St, Finance City, FC 12345</p>
                                    </div>
                                </div>
                            </section>

                        </div>
                    </div>
                </div>
            </div>

            {/* FAQ Section */}
            <FAQ
                title="Privacy & Security Questions"
                subtitle="Common questions about how we protect your personal information and data"
                faqs={[
                    {
                        id: 1,
                        question: "How does Diasporawise protect my personal information?",
                        answer: "We use industry-standard encryption, secure servers, and strict access controls to protect your personal information. All data is encrypted both in transit and at rest, and we regularly conduct security audits to ensure the highest level of protection for your sensitive information."
                    },
                    {
                        id: 2,
                        question: "What personal information do you collect and why?",
                        answer: "We collect only the information necessary to provide our services, including contact details, financial information for investment purposes, and identity verification data. This information is used solely for account management, investment processing, and regulatory compliance requirements."
                    },
                    {
                        id: 3,
                        question: "Do you share my information with third parties?",
                        answer: "We do not sell or share your personal information with third parties for marketing purposes. We may share information only with trusted service providers who help us operate our platform, and only to the extent necessary for them to perform their services. All third parties are bound by strict confidentiality agreements."
                    },
                    {
                        id: 4,
                        question: "How long do you keep my personal data?",
                        answer: "We retain your personal data only as long as necessary to provide our services and comply with legal and regulatory requirements. Financial records are typically retained for 7 years as required by law, while other personal information may be deleted upon account closure or upon your request, subject to legal requirements."
                    },
                    {
                        id: 5,
                        question: "Can I access, update, or delete my personal information?",
                        answer: "Yes, you have the right to access, update, or delete your personal information. You can manage most of your information through your account dashboard, or contact our support team for assistance with data requests. We will respond to such requests within 30 days as required by applicable privacy laws."
                    },
                    {
                        id: 6,
                        question: "How do you handle data breaches?",
                        answer: "In the unlikely event of a data breach, we have comprehensive incident response procedures in place. We will notify affected users and relevant authorities within 72 hours as required by law, and take immediate steps to contain and remediate any security incident to protect your information."
                    }
                ]}
            />

            <Footer />
        </div>
    );
};

export default PrivacyPolicy; 